import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  Mail,
  Phone,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Shield,
  Globe,
  Bell,
  Loader2,
  Rocket,
  AlertTriangle,
  X,
  RefreshCw,
  Signal,
  MessageCircle,
  AtSign,
  User,
  Trash2,
  Plus,
} from 'lucide-react'
import { AnalystNavigation } from '../components/Navigation'
import { ProtectedRoute } from '../components/ProtectedRoute'
import { ConnectionStatusBadge } from '../components/ConnectionStatusBadge'
import { ProgressTracker } from '../components/ProgressTracker'
import { GWSDelegationGuide } from '../components/GWSDelegationGuide'
import { cn } from '../lib/utils'
import {
  createFamily,
  getOnboardingProgress,
  getConnectionStatus,
  regenerateM365Consent,
  testGWSConnection,
  goLive,
  type CreateFamilyResponse,
  type OnboardingProgressResponse,
  type ConnectionStatusResponse,
} from '../lib/api'

// ── Types ───────────────────────────────────────────────────────────

interface HouseholdMemberForm {
  name: string
  email: string
  role: 'spouse' | 'child' | 'staff' | ''
}

interface FormData {
  family_name: string
  primary_email: string
  primary_contact_name: string
  phone: string
  connect_m365: boolean
  m365_domain: string
  connect_gws: boolean
  gws_domain: string
  gws_admin_email: string
  autonomy_tier: number
  alert_channels: string[]
  household_members: HouseholdMemberForm[]
}

interface CreationResult {
  family: CreateFamilyResponse
  progress: OnboardingProgressResponse
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error'
type TestConnectionState = 'idle' | 'testing' | 'success' | 'error'
type GoLiveState = 'idle' | 'confirming' | 'submitting' | 'success' | 'error'

// ── Constants ───────────────────────────────────────────────────────

const INITIAL_FORM: FormData = {
  family_name: '',
  primary_email: '',
  primary_contact_name: '',
  phone: '',
  connect_m365: false,
  m365_domain: '',
  connect_gws: false,
  gws_domain: '',
  gws_admin_email: '',
  autonomy_tier: 0,
  alert_channels: ['email', 'signal'],
  household_members: [],
}

const TIER_OPTIONS = [
  {
    value: 0,
    label: 'Tier 0 — Notify only',
    description: 'The team tells you. This surface executes nothing.',
    icon: Bell,
  },
  {
    value: 1,
    label: 'Tier 1 — Preference only',
    description: 'Noted for later policy. Response is not armed.',
    icon: Shield,
  },
  {
    value: 2,
    label: 'Tier 2 — Preference only',
    description: 'Noted for later policy. This surface executes nothing.',
    icon: CheckCircle2,
  },
  {
    value: 3,
    label: 'Tier 3 — Preference only',
    description: 'Stage 3 is locked. Response stays disarmed.',
    icon: Rocket,
  },
]

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'signal', label: 'Signal', icon: Signal },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
]

const ROLE_OPTIONS = [
  { value: 'spouse', label: 'Spouse / Partner' },
  { value: 'child', label: 'Child' },
  { value: 'staff', label: 'Staff' },
  { value: '', label: 'Select role...' },
]

// Progress is refetched on every poll tick. It is a small, flat, same-shaped
// payload from a single endpoint, so a stable stringify compare is enough to
// tell "nothing changed" from a real update — and cheap enough to run each
// tick. We use it to avoid replacing `result` (and churning its identity) when
// the backend returns progress we already have.
function isProgressEqual(
  a: OnboardingProgressResponse,
  b: OnboardingProgressResponse,
): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

// ── Main Page Component ─────────────────────────────────────────────

function OnboardFamilyPageContent() {
  const navigate = useNavigate()

  // ── Form State ──
  const [form, setForm] = useState<FormData>({ ...INITIAL_FORM })
  const [showAdvanced, setShowAdvanced] = useState(false)

  // ── Submission State ──
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<CreationResult | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Mirror `result` into a ref so the polling callback can read the current
  // family without depending on `result`'s identity. Written during render
  // (idempotent) so it is always current before any effect runs.
  const resultRef = useRef<CreationResult | null>(null)
  resultRef.current = result

  // ── Connection Status Polling ──
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusResponse | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── GWS Test ──
  const [gwsTestState, setGwsTestState] = useState<TestConnectionState>('idle')
  const [gwsTestMessage, setGwsTestMessage] = useState<string | undefined>()

  // ── Go Live ──
  const [goLiveState, setGoLiveState] = useState<GoLiveState>('idle')
  const [goLiveError, setGoLiveError] = useState<string | null>(null)
  const [goLiveResult, setGoLiveResult] = useState<{
    accounts_monitored: number
    platforms: string[]
    welcome_alert_id: string
  } | null>(null)

  // ── Show confetti ──
  const [showConfetti, setShowConfetti] = useState(false)

  // ── Form helpers ──
  const updateForm = useCallback((updates: Partial<FormData>) => {
    setForm((prev) => ({ ...prev, ...updates }))
  }, [])

  const addHouseholdMember = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      household_members: [...prev.household_members, { name: '', email: '', role: '' }],
    }))
  }, [])

  const removeHouseholdMember = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      household_members: prev.household_members.filter((_, i) => i !== index),
    }))
  }, [])

  const updateHouseholdMember = useCallback(
    (index: number, updates: Partial<HouseholdMemberForm>) => {
      setForm((prev) => ({
        ...prev,
        household_members: prev.household_members.map((m, i) =>
          i === index ? { ...m, ...updates } : m,
        ),
      }))
    },
    [],
  )

  const toggleChannel = useCallback((channel: string) => {
    setForm((prev) => {
      const has = prev.alert_channels.includes(channel)
      return {
        ...prev,
        alert_channels: has
          ? prev.alert_channels.filter((c) => c !== channel)
          : [...prev.alert_channels, channel],
      }
    })
  }, [])

  // ── Copy to clipboard ──
  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      console.warn('Clipboard API not available')
    }
  }, [])

  // ── Form validation ──
  const isFormValid = useCallback((): boolean => {
    if (!form.family_name.trim()) return false
    if (!form.primary_email.trim() || !form.primary_email.includes('@')) return false
    if (!form.primary_contact_name.trim()) return false
    if (form.connect_m365 && !form.m365_domain.trim()) return false
    if (form.connect_gws && (!form.gws_domain.trim() || !form.gws_admin_email.trim())) return false
    if (form.alert_channels.length === 0) return false
    return true
  }, [form])

  // ── Submit: Create family ──
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!isFormValid()) return

      setSubmitState('submitting')
      setSubmitError(null)

      try {
        const payload = {
          family_name: form.family_name,
          primary_email: form.primary_email,
          primary_contact_name: form.primary_contact_name,
          phone: form.phone || undefined,
          m365_domain: form.connect_m365 ? form.m365_domain : undefined,
          gws_domain: form.connect_gws ? form.gws_domain : undefined,
          gws_admin_email: form.connect_gws ? form.gws_admin_email : undefined,
          autonomy_tier: form.autonomy_tier,
          alert_channels: form.alert_channels,
          household_members: form.household_members
            .filter((m) => m.name.trim() && m.email.trim())
            .map((m) => ({
              name: m.name,
              email: m.email,
              role: m.role || 'other',
            })),
        }

        const family = await createFamily(payload)

        // Fetch initial progress
        const progress = await getOnboardingProgress(family.onboarding_token)

        setResult({ family, progress })
        setSubmitState('success')
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to create family'
        setSubmitError(msg)
        setSubmitState('error')
      }
    },
    [form, isFormValid],
  )

  // ── Poll connection status ──
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  // Stable across renders (empty deps): it reads the live family from the ref,
  // so a progress update never recreates this callback and never re-triggers
  // the polling effect below. Crucially, `result` is only replaced when the
  // fetched progress actually differs — an unchanged poll keeps the same
  // `result` identity, so nothing downstream churns.
  const refreshConnectionStatus = useCallback(async () => {
    const current = resultRef.current
    if (!current) return
    setIsRefreshing(true)
    try {
      const [status, progress] = await Promise.all([
        getConnectionStatus(current.family.family_id),
        getOnboardingProgress(current.family.onboarding_token),
      ])
      setConnectionStatus(status)
      setResult((prev) =>
        prev && !isProgressEqual(prev.progress, progress)
          ? { ...prev, progress }
          : prev,
      )
    } catch {
      // Silently fail — user can retry
    } finally {
      setIsRefreshing(false)
    }
  }, [])

  // Start polling when a family exists. Gated on the primitive family_id (and
  // go-live state), NOT the whole `result` object — so progress updates within
  // the same family don't tear down and re-arm the interval. Both callbacks are
  // stable, so this effect subscribes once per family and settles.
  const familyId = result?.family.family_id ?? null
  useEffect(() => {
    if (!familyId || goLiveState === 'success') {
      stopPolling()
      return
    }

    // Immediate first fetch
    refreshConnectionStatus()

    // Poll every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      refreshConnectionStatus()
    }, 5000)

    return () => stopPolling()
  }, [familyId, goLiveState, refreshConnectionStatus, stopPolling])

  // ── Regenerate M365 consent ──
  const handleRegenerateM365 = useCallback(async () => {
    if (!result || !connectionStatus) return
    const m365Account = connectionStatus.accounts.find(
      (a) => a.tenant_type === 'microsoft365',
    )
    if (!m365Account) return

    try {
      const resp = await regenerateM365Consent(m365Account.account_id)
      setResult((prev) =>
        prev
          ? { ...prev, family: { ...prev.family, m365_consent_url: resp.consent_url } }
          : prev,
      )
    } catch {
      setSubmitError('Failed to regenerate consent link')
    }
  }, [result, connectionStatus])

  // ── Test GWS connection ──
  const handleTestGWS = useCallback(async () => {
    if (!result || !connectionStatus) return
    const gwsAccount = connectionStatus.accounts.find(
      (a) => a.tenant_type === 'google_workspace',
    )
    if (!gwsAccount) return

    setGwsTestState('testing')
    setGwsTestMessage(undefined)

    try {
      const resp = await testGWSConnection(gwsAccount.account_id)
      setGwsTestState(resp.status === 'active' ? 'success' : 'error')
      setGwsTestMessage(resp.message)
      // Refresh connection status after test
      await refreshConnectionStatus()
    } catch {
      setGwsTestState('error')
      setGwsTestMessage('Connection test failed. Please check your delegation settings.')
    }
  }, [result, connectionStatus, refreshConnectionStatus])

  // ── Go Live ──
  const handleGoLive = useCallback(async () => {
    if (!result) return
    setGoLiveState('submitting')
    setGoLiveError(null)

    try {
      const resp = await goLive(result.family.family_id)
      setGoLiveResult({
        accounts_monitored: resp.accounts_monitored,
        platforms: resp.platforms,
        welcome_alert_id: resp.welcome_alert_id,
      })
      setGoLiveState('success')
      setShowConfetti(true)
      // Trigger one final progress refresh
      const progress = await getOnboardingProgress(result.family.onboarding_token)
      setResult((prev) =>
        prev && !isProgressEqual(prev.progress, progress)
          ? { ...prev, progress }
          : prev,
      )
      // Stop confetti after a few seconds
      setTimeout(() => setShowConfetti(false), 5000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to mark household live'
      setGoLiveError(msg)
      setGoLiveState('error')
    }
  }, [result])

  // ── Reset to create another family ──
  const handleReset = useCallback(() => {
    setForm({ ...INITIAL_FORM })
    setSubmitState('idle')
    setSubmitError(null)
    setResult(null)
    setConnectionStatus(null)
    setGwsTestState('idle')
    setGwsTestMessage(undefined)
    setGoLiveState('idle')
    setGoLiveError(null)
    setGoLiveResult(null)
    setShowConfetti(false)
    stopPolling()
  }, [stopPolling])

  // ── Progress steps for tracker ──
  const progressSteps = result
    ? result.progress.steps.map((s) => ({
        id: s.id,
        label: s.label,
        status: s.status as 'completed' | 'pending' | 'active' | 'error',
      }))
    : []

  const canGoLive = result?.progress.can_go_live ?? false

  // ── Render ──
  return (
    <div className="min-h-screen bg-cream">
      <AnalystNavigation />

      {/* Confetti overlay */}
      <AnimatePresence>
        {showConfetti && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none z-50"
          >
            {Array.from({ length: 40 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-sm"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: -10,
                  backgroundColor: ['#a07a3b', '#0a1620', '#16a34a', '#3b82f6', '#dc2626'][
                    i % 5
                  ],
                }}
                initial={{ y: 0, x: 0, rotate: 0, opacity: 1 }}
                animate={{
                  y: window.innerHeight + 20,
                  x: (Math.random() - 0.5) * 200,
                  rotate: Math.random() * 720 - 360,
                  opacity: [1, 1, 0],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  delay: Math.random() * 0.5,
                  ease: 'easeOut',
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brass/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-brass" aria-hidden="true" />
            </div>
            <div>
              <h1
                className="text-xl sm:text-2xl font-bold text-navy"
                style={{ fontFamily: 'Liter, serif' }}
              >
                White-Glove Onboarding
              </h1>
              <p className="text-sm text-gray-500">
                Create a new family and generate consent links
              </p>
            </div>
          </div>
        </motion.div>

        {/* ── POST-CREATE VIEW ── */}
        <AnimatePresence mode="wait">
          {result && (
            <motion.div
              key="post-create"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Go Live Success Banner */}
              <AnimatePresence>
                {goLiveState === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-green-50 border-2 border-green-300 rounded-xl p-6 text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', damping: 10, stiffness: 200 }}
                      className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"
                    >
                      <CheckCircle2 className="w-8 h-8 text-green-600" aria-hidden="true" />
                    </motion.div>
                    <h2
                      className="text-xl font-bold text-green-800 mb-1"
                      style={{ fontFamily: 'Liter, serif' }}
                    >
                      Household marked live
                    </h2>
                    <p className="text-sm text-green-700 mb-3">
                      Marked live with {goLiveResult?.platforms.length} platform(s) and{' '}
                      {goLiveResult?.accounts_monitored} account(s) connected.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/analyst')}
                      className="tap-min inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
                    >
                      <Shield className="w-4 h-4" aria-hidden="true" />
                      Go to Analyst Dashboard
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Family Summary Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                    <h2
                      className="text-sm font-semibold text-navy"
                      style={{ fontFamily: 'Liter, serif' }}
                    >
                      {form.family_name}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="tap-min flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs text-navy hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="w-3 h-3" aria-hidden="true" />
                      New Family
                    </button>
                  </div>
                </div>
                <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Contact</p>
                    <p className="text-navy font-medium">{form.primary_contact_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-navy font-medium break-all">{form.primary_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tenant</p>
                    <p className="font-mono text-xs text-navy">{result.family.tenant_slug}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Autonomy</p>
                    <p className="text-navy font-medium">Tier {form.autonomy_tier}</p>
                  </div>
                </div>
              </div>

              {/* Progress Tracker */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    className="text-sm font-semibold text-navy"
                    style={{ fontFamily: 'Liter, serif' }}
                  >
                    Onboarding Progress
                  </h3>
                  <button
                    type="button"
                    onClick={refreshConnectionStatus}
                    disabled={isRefreshing}
                    className="tap-min flex items-center gap-1 text-xs text-brass hover:text-brass-dark font-medium disabled:opacity-50"
                  >
                    <RefreshCw
                      className={cn('w-3 h-3', isRefreshing && 'animate-spin')}
                      aria-hidden="true"
                    />
                    Refresh
                  </button>
                </div>
                <ProgressTracker steps={progressSteps} size="md" />
              </div>

              {/* Connection Status Cards */}
              {connectionStatus && connectionStatus.accounts.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-4">
                  <h3
                    className="text-sm font-semibold text-navy"
                    style={{ fontFamily: 'Liter, serif' }}
                  >
                    Connection Status
                  </h3>
                  {connectionStatus.accounts.map((account) => (
                    <div
                      key={account.account_id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border border-gray-100 bg-gray-50/50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                            account.tenant_type === 'microsoft365'
                              ? 'bg-blue-50'
                              : 'bg-green-50',
                          )}
                        >
                          {account.tenant_type === 'microsoft365' ? (
                            <Globe className="w-4 h-4 text-blue-600" aria-hidden="true" />
                          ) : (
                            <Mail className="w-4 h-4 text-green-600" aria-hidden="true" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">
                            {account.tenant_type === 'microsoft365'
                              ? 'Microsoft 365'
                              : 'Google Workspace'}
                          </p>
                          <p className="text-xs text-gray-500">{account.upn}</p>
                        </div>
                      </div>
                      <ConnectionStatusBadge
                        status={account.connection_status}
                        error={account.connection_error}
                        lastSyncAt={account.last_sync_at}
                        onRefresh={refreshConnectionStatus}
                        size="md"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* M365 Consent Link */}
              {result.family.m365_consent_url && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl border border-gray-200 p-5"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-blue-600" aria-hidden="true" />
                    <h3
                      className="text-sm font-semibold text-navy"
                      style={{ fontFamily: 'Liter, serif' }}
                    >
                      Microsoft 365 Consent Link
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Send this link to the primary contact. They will be redirected to Microsoft to
                    grant consent.
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-navy font-mono break-all">
                      {result.family.m365_consent_url}
                    </code>
                    <button
                      type="button"
                      onClick={() => handleCopy(result.family.m365_consent_url!, 'm365-link')}
                      className={cn(
                        'tap-min flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors',
                        copiedField === 'm365-link'
                          ? 'border-green-300 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-navy hover:bg-gray-50',
                      )}
                    >
                      {copiedField === 'm365-link' ? (
                        <>
                          <Check className="w-3.5 h-3.5" aria-hidden="true" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleRegenerateM365}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-brass hover:text-brass-dark font-medium transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" aria-hidden="true" />
                    Regenerate link
                  </button>
                </motion.div>
              )}

              {/* GWS Delegation Guide */}
              {result.family.gws_delegation_guide && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <GWSDelegationGuide
                    guide={{
                      service_account_email:
                        result.family.gws_delegation_guide.service_account_email,
                      admin_console_url:
                        result.family.gws_delegation_guide.admin_console_url,
                      steps: result.family.gws_delegation_guide.steps,
                      scopes: result.family.gws_delegation_guide.scopes,
                      scopes_copyable: result.family.gws_delegation_guide.scopes_copyable,
                    }}
                    onTestConnection={handleTestGWS}
                    testStatus={gwsTestState}
                    testMessage={gwsTestMessage}
                  />
                </motion.div>
              )}

              {/* Go Live Section */}
              {goLiveState !== 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                        canGoLive ? 'bg-brass/10' : 'bg-gray-100',
                      )}
                    >
                      <Rocket
                        className={cn(
                          'w-5 h-5',
                          canGoLive ? 'text-brass' : 'text-gray-400',
                        )}
                        aria-hidden="true"
                      />
                    </div>
                    <div>
                      <h3
                        className="text-sm font-semibold text-navy"
                        style={{ fontFamily: 'Liter, serif' }}
                      >
                        Mark Household Live
                      </h3>
                      <p className="text-xs text-gray-500">
                        {canGoLive
                          ? 'All connections are ready. Click Go Live to mark this household live.'
                          : 'All platform connections must be active before going live.'}
                      </p>
                    </div>
                  </div>

                  {/* Go Live confirmation dialog */}
                  <AnimatePresence>
                    {(goLiveState === 'confirming' || goLiveState === 'submitting') && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-4"
                      >
                        <div className="bg-brass/5 border border-brass/20 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-brass flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-navy">
                                Mark this household live?
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                This marks <strong>{form.family_name}</strong> as connected and live
                                in DigitalWatch. Response remains disarmed, and this step does not
                                send anything to the family.
                              </p>
                              <div className="flex gap-2 mt-3">
                                <button
                                  type="button"
                                  onClick={handleGoLive}
                                  disabled={goLiveState === 'submitting'}
                                  className="tap-min flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brass text-white text-sm font-semibold hover:bg-brass-dark disabled:opacity-50 transition-colors"
                                >
                                  {goLiveState === 'submitting' ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Marking live...
                                    </>
                                  ) : (
                                    <>
                                      <Rocket className="w-4 h-4" />
                                      Yes, Go Live
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setGoLiveState('idle')}
                                  disabled={goLiveState === 'submitting'}
                                  className="tap-min px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {goLiveError && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100"
                    >
                      {goLiveError}
                    </motion.p>
                  )}

                  <button
                    type="button"
                    onClick={() => setGoLiveState('confirming')}
                    disabled={
                      !canGoLive ||
                      goLiveState === 'confirming' ||
                      goLiveState === 'submitting'
                    }
                    className={cn(
                      'tap-min w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-bold transition-all',
                      canGoLive
                        ? 'bg-brass text-white hover:bg-brass-dark shadow-lg shadow-brass/20 hover:shadow-xl hover:shadow-brass/30 hover:scale-[1.02] active:scale-[0.98]'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed',
                    )}
                  >
                    {canGoLive ? (
                      <>
                        <Rocket className="w-5 h-5" aria-hidden="true" />
                        Go Live — Mark Household Live
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5" aria-hidden="true" />
                        Waiting for connections...
                      </>
                    )}
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ── CREATE FORM VIEW ── */}
          {!result && (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Submit error */}
                <AnimatePresence>
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2"
                    >
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{submitError}</p>
                      <button
                        type="button"
                        onClick={() => setSubmitError(null)}
                        className="flex-shrink-0 text-red-400 hover:text-red-600"
                        aria-label="Dismiss error"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* ═══ Section A: Family Information ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h2
                      className="text-sm font-semibold text-navy flex items-center gap-2"
                      style={{ fontFamily: 'Liter, serif' }}
                    >
                      <Building2 className="w-4 h-4 text-brass" aria-hidden="true" />
                      Family Information
                    </h2>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* Family Name */}
                    <div>
                      <label
                        htmlFor="family-name"
                        className="block text-sm font-medium text-navy mb-1"
                      >
                        Family Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="family-name"
                        type="text"
                        value={form.family_name}
                        onChange={(e) => updateForm({ family_name: e.target.value })}
                        placeholder="e.g., Alexander Family"
                        required
                        className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                      />
                    </div>

                    {/* Primary Contact Name */}
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-navy mb-1"
                      >
                        Primary Contact Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                          aria-hidden="true"
                        />
                        <input
                          id="contact-name"
                          type="text"
                          value={form.primary_contact_name}
                          onChange={(e) => updateForm({ primary_contact_name: e.target.value })}
                          placeholder="e.g., Paul Alexander"
                          required
                          className="w-full tap-min pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Primary Email */}
                    <div>
                      <label
                        htmlFor="primary-email"
                        className="block text-sm font-medium text-navy mb-1"
                      >
                        Primary Contact Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                          aria-hidden="true"
                        />
                        <input
                          id="primary-email"
                          type="email"
                          value={form.primary_email}
                          onChange={(e) => updateForm({ primary_email: e.target.value })}
                          placeholder="e.g., paul@alexanderfamily.com"
                          required
                          className="w-full tap-min pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-navy mb-1">
                        Phone <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <div className="relative">
                        <Phone
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                          aria-hidden="true"
                        />
                        <input
                          id="phone"
                          type="tel"
                          value={form.phone}
                          onChange={(e) => updateForm({ phone: e.target.value })}
                          placeholder="+1-555-0123"
                          className="w-full tap-min pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* ═══ Section B: Microsoft 365 ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <h2
                        className="text-sm font-semibold text-navy flex items-center gap-2"
                        style={{ fontFamily: 'Liter, serif' }}
                      >
                        <Globe className="w-4 h-4 text-blue-600" aria-hidden="true" />
                        Microsoft 365
                      </h2>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.connect_m365}
                          onChange={(e) => updateForm({ connect_m365: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div
                          className={cn(
                            'w-11 h-6 rounded-full transition-colors',
                            form.connect_m365 ? 'bg-brass' : 'bg-gray-300',
                            'after:content-[""] after:absolute after:top-0.5 after:left-0.5',
                            'after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform',
                            'after:shadow-sm',
                            form.connect_m365 && 'after:translate-x-5',
                          )}
                        />
                      </label>
                    </div>
                  </div>

                  <AnimatePresence>
                    {form.connect_m365 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="p-5">
                          <label
                            htmlFor="m365-domain"
                            className="block text-sm font-medium text-navy mb-1"
                          >
                            M365 Domain <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <AtSign
                              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                              aria-hidden="true"
                            />
                            <input
                              id="m365-domain"
                              type="text"
                              value={form.m365_domain}
                              onChange={(e) => updateForm({ m365_domain: e.target.value })}
                              placeholder="alexanderfamily.com"
                              required={form.connect_m365}
                              className="w-full tap-min pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            After creation, a consent link will be generated for the primary contact
                            to authorize DigitalWatch to access their Microsoft 365 account.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* ═══ Section C: Google Workspace ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <h2
                        className="text-sm font-semibold text-navy flex items-center gap-2"
                        style={{ fontFamily: 'Liter, serif' }}
                      >
                        <Mail className="w-4 h-4 text-green-600" aria-hidden="true" />
                        Google Workspace
                      </h2>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.connect_gws}
                          onChange={(e) => updateForm({ connect_gws: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div
                          className={cn(
                            'w-11 h-6 rounded-full transition-colors',
                            form.connect_gws ? 'bg-brass' : 'bg-gray-300',
                            'after:content-[""] after:absolute after:top-0.5 after:left-0.5',
                            'after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform',
                            'after:shadow-sm',
                            form.connect_gws && 'after:translate-x-5',
                          )}
                        />
                      </label>
                    </div>
                  </div>

                  <AnimatePresence>
                    {form.connect_gws && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="p-5 space-y-4">
                          <div>
                            <label
                              htmlFor="gws-domain"
                              className="block text-sm font-medium text-navy mb-1"
                            >
                              GWS Domain <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <AtSign
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                aria-hidden="true"
                              />
                              <input
                                id="gws-domain"
                                type="text"
                                value={form.gws_domain}
                                onChange={(e) => updateForm({ gws_domain: e.target.value })}
                                placeholder="alexanderfamily.com"
                                required={form.connect_gws}
                                className="w-full tap-min pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                              />
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="gws-admin-email"
                              className="block text-sm font-medium text-navy mb-1"
                            >
                              GWS Admin Email <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <User
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                aria-hidden="true"
                              />
                              <input
                                id="gws-admin-email"
                                type="email"
                                value={form.gws_admin_email}
                                onChange={(e) =>
                                  updateForm({ gws_admin_email: e.target.value })
                                }
                                placeholder="admin@alexanderfamily.com"
                                required={form.connect_gws}
                                className="w-full tap-min pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              After creation, you will receive a step-by-step guide to configure
                              domain-wide delegation in Google Admin Console.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* ═══ Section D: Household Members ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h2
                      className="text-sm font-semibold text-navy flex items-center gap-2"
                      style={{ fontFamily: 'Liter, serif' }}
                    >
                      <Users className="w-4 h-4 text-brass" aria-hidden="true" />
                      Household Members
                    </h2>
                  </div>
                  <div className="p-5 space-y-3">
                    <AnimatePresence>
                      {form.household_members.map((member, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/30 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-500">
                                Member {index + 1}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeHouseholdMember(index)}
                                className="tap-min text-red-500 hover:text-red-700 transition-colors"
                                aria-label={`Remove member ${index + 1}`}
                              >
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-navy mb-1">
                                  Name
                                </label>
                                <input
                                  type="text"
                                  value={member.name}
                                  onChange={(e) =>
                                    updateHouseholdMember(index, { name: e.target.value })
                                  }
                                  placeholder="Full name"
                                  className="w-full tap-min px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-navy mb-1">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={member.email}
                                  onChange={(e) =>
                                    updateHouseholdMember(index, { email: e.target.value })
                                  }
                                  placeholder="email@domain.com"
                                  className="w-full tap-min px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-navy mb-1">
                                  Role
                                </label>
                                <select
                                  value={member.role}
                                  onChange={(e) =>
                                    updateHouseholdMember(index, {
                                      role: e.target.value as HouseholdMemberForm['role'],
                                    })
                                  }
                                  className="w-full tap-min px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                                >
                                  {ROLE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    <button
                      type="button"
                      onClick={addHouseholdMember}
                      className="tap-min w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-brass hover:text-brass transition-colors"
                    >
                      <Plus className="w-4 h-4" aria-hidden="true" />
                      Add household member
                    </button>
                  </div>
                </motion.div>

                {/* ═══ Section E: Autonomy & Channels ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((v) => !v)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left"
                  >
                    <h2
                      className="text-sm font-semibold text-navy flex items-center gap-2"
                      style={{ fontFamily: 'Liter, serif' }}
                    >
                      <Shield className="w-4 h-4 text-brass" aria-hidden="true" />
                      Autonomy &amp; Alert Channels
                    </h2>
                    {showAdvanced ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
                    )}
                  </button>

                  <AnimatePresence>
                    {showAdvanced && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 space-y-5">
                          {/* Tier selection */}
                          <div>
                            <label className="block text-sm font-medium text-navy mb-2">
                              Autonomy Tier
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {TIER_OPTIONS.map((tier) => {
                                const Icon = tier.icon
                                const isSelected = form.autonomy_tier === tier.value
                                return (
                                  <button
                                    key={tier.value}
                                    type="button"
                                    onClick={() => updateForm({ autonomy_tier: tier.value })}
                                    className={cn(
                                      'tap-min flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all',
                                      isSelected
                                        ? 'border-brass bg-brass/5'
                                        : 'border-gray-200 hover:border-gray-300',
                                    )}
                                  >
                                    <Icon
                                      className={cn(
                                        'w-5 h-5 flex-shrink-0 mt-0.5',
                                        isSelected ? 'text-brass' : 'text-gray-400',
                                      )}
                                      aria-hidden="true"
                                    />
                                    <div>
                                      <p className="text-sm font-medium text-navy">{tier.label}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {tier.description}
                                      </p>
                                    </div>
                                  </button>
                                )
                              })}
                            </div>
                          </div>

                          {/* Alert channels */}
                          <div className="pt-3 border-t border-gray-100">
                            <label className="block text-sm font-medium text-navy mb-2">
                              Alert Channels
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {CHANNEL_OPTIONS.map((ch) => {
                                const Icon = ch.icon
                                const isActive = form.alert_channels.includes(ch.value)
                                return (
                                  <button
                                    key={ch.value}
                                    type="button"
                                    onClick={() => toggleChannel(ch.value)}
                                    className={cn(
                                      'tap-min flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all',
                                      isActive
                                        ? 'border-brass bg-brass/5'
                                        : 'border-gray-200 hover:border-gray-300',
                                    )}
                                  >
                                    <Icon
                                      className={cn(
                                        'w-4 h-4',
                                        isActive ? 'text-brass' : 'text-gray-400',
                                      )}
                                      aria-hidden="true"
                                    />
                                    <span
                                      className={cn(
                                        'text-sm',
                                        isActive ? 'text-navy font-medium' : 'text-gray-600',
                                      )}
                                    >
                                      {ch.label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* ═══ Section F: Submit Button ═══ */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="pt-2"
                >
                  <button
                    type="submit"
                    disabled={submitState === 'submitting' || !isFormValid()}
                    className={cn(
                      'tap-min w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-bold transition-all',
                      'bg-brass text-white hover:bg-brass-dark shadow-lg shadow-brass/20',
                      'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
                    )}
                  >
                    {submitState === 'submitting' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                        Creating Family &amp; Generating Links...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
                        Create Family &amp; Generate Consent Links
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-gray-400 mt-2">
                    Creates the tenant, principal, household, and generates platform consent links.
                  </p>
                </motion.div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  )
}

// ── Exported Page with Route Guard ──────────────────────────────────

export function OnboardFamilyPage() {
  return (
    <ProtectedRoute requiredRole="analyst">
      <OnboardFamilyPageContent />
    </ProtectedRoute>
  )
}
